import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { AccountMarketplace, Marketplace } from "@front/m19-api-client";
import { ProductGroupEx } from "@front/m19-models";
import { AccountSelectionService, ProductGroupService } from "@front/m19-services";
import {
  IButtonDirective,
  IDataTableComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  ITableDataSource,
  ITableImports,
  ITableTextFilterConfig,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { forkJoin, switchMap } from "rxjs";
import {
  BulkImportService,
  ProductGroupBulkOperations,
  ProductGroupUploadResult,
} from "../strategy-bulk-upload-modal/bulk-import.service";

import { NgClass } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ProductGroupBulkUploadComponent,
  ProductGroupBulkUploadModalData,
} from "../strategy-bulk-upload-modal/product-group-bulk-upload/product-group-bulk-upload.component";
import {
  ProductGroupBulkUploadReportModalData,
  ProductGroupReportModalComponent,
} from "../strategy-bulk-upload-modal/product-group-bulk-upload/product-group-report-modal.component";
import {
  ProductGroupBulkUploadResultModalComponent,
  ProductGroupBulkUploadResultModalData,
} from "./product-group-bulk/product-group-bulk-upload-result-modal.component";
import { ProductGroupListItemComponent } from "./product-group-list-item/product-group-list-item.component";

export interface ProductGroupCreationModalData {
  accountId: string;
  marketplace: Marketplace;
}

/* Modal component to create a new Product Group*/
@Component({
  selector: "app-modal-content",
  templateUrl: "./product-group-creation.component.html",
  imports: [TranslocoDirective, FormsModule, IButtonDirective, IFormFieldComponent, ILabelComponent, IInputDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCreateProductGroupComponent {
  productGroupService = inject(ProductGroupService);
  private toastrService = inject(ToasterService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);

  protected readonly modalData = inject(MODAL_DATA) as ProductGroupCreationModalData;
  modalRef = inject(ModalRef);

  pgName?: string;

  private gotoProductGroupPage(productGroup: ProductGroupEx, marketplace: Marketplace) {
    this.router.navigate([`/product-center/product-group/${productGroup.productGroupId}`], {
      queryParams: { marketplace: marketplace },
    });
  }

  create() {
    this.productGroupService
      .createProductGroup(this.modalData.accountId, this.modalData.marketplace, this.pgName!)
      .subscribe({
        next: (productGroup) => {
          this.gotoProductGroupPage(productGroup, this.modalData.marketplace);
          this.toastrService.success(this.translocoService.translate("product-group.product_group_created"));
          this.modalRef.close();
        },
        error: (error: string) => {
          this.toastrService.error(
            error,
            this.translocoService.translate("product-group.product_group_creation_error"),
          );
          this.modalRef.close();
        },
      });
  }
}

export interface ProductGroupDeletionModalData {
  productGroups: ProductGroupEx[];
  accountMarketplace: AccountMarketplace;
}

@Component({
  selector: "app-modal-content",
  templateUrl: "./product-group-deletion.component.html",
  imports: [TranslocoDirective, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDeleteProductGroupComponent {
  private productGroupService = inject(ProductGroupService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  protected readonly modalData = inject(MODAL_DATA) as ProductGroupDeletionModalData;
  modalRef = inject(ModalRef);

  loading = signal<boolean>(false);

  delete() {
    this.loading.set(true);
    forkJoin(
      this.modalData.productGroups.map((pg) =>
        this.productGroupService.deleteProductGroup(
          this.modalData.accountMarketplace.accountId,
          this.modalData.accountMarketplace.marketplace,
          pg.productGroupId!,
        ),
      ),
    ).subscribe({
      next: () => {
        this.toastrService.success(this.translocoService.translate("product-group.product_group_deleted"));
        this.modalRef.close(true);
        this.loading.set(false);
      },
      error: (error: string) => {
        this.toastrService.error(error, this.translocoService.translate("product-group.product_group_deletion_error"));
        this.loading.set(false);
      },
    });
  }
}

@Component({
  selector: "app-product-group",
  templateUrl: "./product-group.component.html",
  imports: [
    TranslocoDirective,
    ITableImports,
    IDataTableComponent,
    ProductGroupListItemComponent,
    NgClass,
    IButtonDirective,
    FormsModule,
    IDropdownMenuTriggerDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupComponent {
  private accountSelectionService = inject(AccountSelectionService);
  private readonly productGroupService = inject(ProductGroupService);
  private readonly modalService = inject(ModalService);
  private readonly bulkImportService = inject(BulkImportService);
  private readonly toastrService = inject(ToasterService);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  private readonly productGroups$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.productGroupService.getProductGroups(am.accountId, am.marketplace)),
  );
  private readonly productGroups = toSignal(this.productGroups$);
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  loading = signal<boolean>(false);
  readonly selectedRows = signal<Set<ProductGroupEx>>(new Set());

  private readonly textFilterConfig = new ITableTextFilterConfig<ProductGroupEx>((productGroup, filter) => {
    return productGroup.productGroupName.toLowerCase().includes(filter.toLowerCase());
  });
  readonly dataSource = new ITableDataSource<ProductGroupEx>(
    this.productGroups$,
    undefined,
    undefined,
    this.textFilterConfig,
  );

  onRowCheckboxClicked(productGroup: ProductGroupEx) {
    this.selectedRows.update((selectedRows) => {
      if (selectedRows.has(productGroup)) {
        selectedRows.delete(productGroup);
      } else {
        selectedRows.add(productGroup);
      }
      return new Set([...selectedRows]);
    });
  }

  createProductGroup(): void {
    this.modalService.openModal<ProductGroupCreationModalData, void>(ModalCreateProductGroupComponent, {
      size: "sm",
      modalTitle: this.translocoService.translate("product-group-creation.product_group_creation"),
      data: {
        accountId: this.accountMarketplace().accountId,
        marketplace: this.accountMarketplace().marketplace,
      },
    });
  }

  deleteProductGroupsSelection(): void {
    const productGroups = Array.from(this.selectedRows());
    const ref = this.modalService.openModal<ProductGroupDeletionModalData, boolean>(ModalDeleteProductGroupComponent, {
      modalTitle: this.translocoService.translate("product-group-deletion.product_group_deletion"),
      data: {
        productGroups: productGroups,
        accountMarketplace: this.accountMarketplace(),
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedRows.set(new Set());
      }
    });
  }

  openProductGroupPage(productGroup: ProductGroupEx) {
    this.router.navigate([`/product-center/product-group/${productGroup.productGroupId}`], {
      queryParamsHandling: "merge",
    });
  }

  uploadProductGroups(bulkData = "") {
    const ref = this.modalService.openModal<ProductGroupBulkUploadModalData, ProductGroupBulkOperations>(
      ProductGroupBulkUploadComponent,
      {
        size: "lg",
        modalTitle: this.translocoService.translate(
          "product-group-bulk-upload-modal.upload_product_group_configuration",
        ),
        data: {
          accountId: this.accountMarketplace().accountId!,
          marketplace: this.accountMarketplace().marketplace,
          productGroups: this.productGroups() ?? [],
          bulkData,
        },
      },
    );

    ref.afterClosed().subscribe((result) => {
      this.displayBulkOperations(result);
    });
  }

  editProductGroupName(productGroup: ProductGroupEx) {
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("product-group.change_product_group_name"),
      data: {
        initialValue: productGroup.productGroupName,
        maxLength: 80,
      },
    });
    ref
      .afterClosed()
      .pipe(
        switchMap((newName: string) =>
          this.productGroupService.updateProductGroupName(
            this.accountMarketplace().accountId,
            this.accountMarketplace().marketplace,
            productGroup.productGroupId,
            newName,
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("product-group.product_group_name_updated"));
        },
        error: (error: string) => {
          this.toastrService.error(
            error,
            this.translocoService.translate("product-group.product_group_name_update_error"),
          );
        },
      });
  }

  private displayBulkOperations(bulkOperations: ProductGroupBulkOperations) {
    const modalRef = this.modalService.openModal<ProductGroupBulkUploadReportModalData, ProductGroupUploadResult>(
      ProductGroupReportModalComponent,
      {
        size: "lg",
        modalTitle: this.translocoService.translate(
          "product-group-bulk-upload-report-modal.product_group_upload_summary",
        ),
        data: {
          productGroupBulkOperations: bulkOperations,
          accountId: this.accountMarketplace().accountId,
          marketplace: this.accountMarketplace().marketplace,
        },
      },
    );

    modalRef.afterClosed().subscribe((result) => {
      this.displayUploadResult(result);
    });

    modalRef.afterCancelled().subscribe(() => {
      this.uploadProductGroups(bulkOperations.bulkData);
    });
  }

  private displayUploadResult(uploadResult: ProductGroupUploadResult) {
    this.modalService.openModal<ProductGroupBulkUploadResultModalData, void>(
      ProductGroupBulkUploadResultModalComponent,
      {
        size: "lg",
        modalTitle: this.translocoService.translate(
          "product-group-bulk-upload-result-modal.product_group_bulk_upload_report",
        ),
        data: {
          uploadResult: uploadResult,
        },
      },
    );
  }

  exportProductGroups() {
    this.bulkImportService.exportProductGroupCsv(this.accountMarketplace(), this.productGroups() ?? []);
  }
}
